import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { DashboardLayout } from "../../layouts/DashboardLayout"
import { listReportsApi, Report } from "../../features/reports/api/reports.api"
import { REPORT_TYPES, REPORT_TYPE_MAP, STATUS_CONFIG } from "./reportTypes"
import { FlagIcon, ChevronRightIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/16/solid"

const RECENT_LIMIT = 8

const formatDate = (ts: number) =>
  new Date(ts * 1000).toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  })

export const ReportsHubPage = () => {
  const [recent, setRecent] = useState<Report[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    listReportsApi({ limit: RECENT_LIMIT }).then((res) => {
      if (cancelled) return
      setRecent(res.data.items)
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [])

  return (
    <DashboardLayout title="Жалобы">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Категории жалоб</h2>
        <p className="text-sm text-gray-400 mt-0.5">Выберите раздел, чтобы посмотреть жалобы с готовыми фильтрами</p>
      </div>

      {/* ── Category cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        {REPORT_TYPES.map((meta) => (
          <Link
            key={meta.type}
            to={`/reports/${meta.type}`}
            className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
          >
            <div className={`bg-gradient-to-r ${meta.gradient} px-5 py-4`}>
              <span className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center text-white flex-shrink-0">
                {meta.icon}
              </span>
            </div>

            <div className="px-5 py-4">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-gray-900">{meta.label}</h3>
                <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 transition-all" />
              </div>
              <p className="text-xs text-gray-400 leading-relaxed">{meta.description}</p>
            </div>
          </Link>
        ))}
      </div>

      {/* ── Recent activity ── */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b bg-gray-50/60">
          <h3 className="font-semibold text-gray-800">Последние жалобы</h3>
        </div>

        {loading ? (
          <div className="divide-y divide-gray-50">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="px-5 py-4">
                <div className="h-4 bg-gray-100 rounded animate-pulse w-2/3" />
              </div>
            ))}
          </div>
        ) : recent.length === 0 ? (
          <div className="py-16 text-center">
            <FlagIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Жалоб пока нет</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {recent.map((r) => {
              const type = REPORT_TYPE_MAP[r.target_type]
              const status = STATUS_CONFIG[r.status]
              return (
                <Link
                  key={r.id}
                  to={`/reports/${r.target_type}`}
                  className="flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50/60 transition-colors"
                >
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${type.color}`}>
                    {type.icon}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-800 truncate">{r.reason}</p>
                    <p className="text-xs text-gray-400">{type.label} · {formatDate(r.created_at)}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${status.className}`}>
                    {status.icon}
                    {status.label}
                  </span>
                  <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
                </Link>
              )
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}